import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import propertiesService from "../services/propertiesService";
import s3Service from "../services/s3Service";
import {
  validateDocumentFile,
  getDocumentAcceptValue,
} from "../utils/documentValidation";
import {
  formatDateUK,
  addYearsToDate,
  isValidDateUK,
} from "../utils/dateUtils";
import DateInput from "../components/DateInput";
import styles from "./PropertyCertificateCreate.module.css";

const PropertyCertificateCreate = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [property, setProperty] = useState(null);
  const [formData, setFormData] = useState({
    property_id: id,
    name: "",
    document_type_id: "",
    document: null,
    document_key: null,
    document_name: null,
    document_type: null,
    document_size: null,
    date_completed: "",
    date_expiration: "",
    date_contractor_appointment: "",
    visible_to_client: true,
  });
  const [formErrors, setFormErrors] = useState({});
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);

  // Certificate types based on Laravel config - only certificate types
  const propertyCertificateTypes = {
    6: "Gas Safe Certificate",
    7: "NIC Certificate",
    8: "Asbestos Register",
    9: "Fire Risk Assessment",
    10: "Legionella",
    30: "Fire Alarm",
    31: "Emergency Lighting",
    32: "Fire Extinguisher Certificate",
    33: "PAT Testing",
    29: "240V Certificate",
  };

  // Load property data
  useEffect(() => {
    loadPropertyData();
    // Set default expiration date to 1 year from now
    const defaultExpirationDate = addYearsToDate(new Date(), 1);
    setFormData((prev) => ({
      ...prev,
      date_expiration: defaultExpirationDate,
    }));
  }, [id]);

  const loadPropertyData = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await propertiesService.getProperty(id);
      if (response.success) {
        setProperty(response.data);
      } else {
        throw new Error("Failed to load property");
      }
    } catch (error) {
      console.error("Error loading property data:", error);
      setError("Error loading property data");
      toast.error("Error loading property data");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) {
      setFormData((prev) => ({
        ...prev,
        document: null,
        document_key: null,
        document_name: null,
        document_type: null,
        document_size: null,
      }));
      setUploadProgress(0);
      return;
    }

    // Validate file
    const validationResult = validateDocumentFile(file);
    if (!validationResult.isValid) {
      toast.error(validationResult.error);
      e.target.value = "";
      return;
    }

    // Start upload process
    setUploading(true);
    setUploadProgress(0);

    try {
      // Upload file to S3
      const s3Key = await s3Service.uploadDocument(file, {
        folder: "property-certificates",
        onProgress: (progress) => {
          setUploadProgress(progress);
        },
      });

      // Update form data with S3 information
      setFormData((prev) => ({
        ...prev,
        document: file,
        document_key: s3Key,
        document_name: file.name,
        document_type: file.type,
        document_size: file.size,
      }));

      // Clear error when upload succeeds
      if (formErrors.document) {
        setFormErrors((prev) => ({
          ...prev,
          document: "",
        }));
      }

      toast.success("Certificate uploaded successfully!");
    } catch (error) {
      console.error("Error uploading certificate:", error);
      toast.error(error.message || "Failed to upload certificate");
      e.target.value = "";
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = "Name is required";
    }

    if (!formData.document_type_id) {
      errors.document_type_id = "Document type is required";
    }

    if (!formData.document_key) {
      errors.document = "Document is required";
    }

    // Validate dates if provided
    if (formData.date_completed && !isValidDateUK(formData.date_completed)) {
      errors.date_completed = "Please enter a valid date (DD/MM/YYYY)";
    }

    if (formData.date_expiration && !isValidDateUK(formData.date_expiration)) {
      errors.date_expiration = "Please enter a valid date (DD/MM/YYYY)";
    }

    if (
      formData.date_contractor_appointment &&
      !isValidDateUK(formData.date_contractor_appointment)
    ) {
      errors.date_contractor_appointment =
        "Please enter a valid date (DD/MM/YYYY)";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Convert DD/MM/YYYY to YYYY-MM-DD using string manipulation only (no Date objects)
  // This avoids timezone issues where toISOString() shifts dates by converting to UTC
  const toISODateSafe = (dateStr) => {
    if (!dateStr) return "";
    const parts = dateStr.split("/");
    if (parts.length !== 3) return dateStr;
    const [day, month, year] = parts;
    return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix the errors in the form");
      return;
    }

    setSubmitting(true);
    try {
      // Create JSON data with S3 key information
      const submitData = {
        property_id: property.id,
        name: formData.name,
        document_type_id: formData.document_type_id,
        document_key: formData.document_key,
        document_name: formData.document_name,
        document_type: formData.document_type,
        document_size: formData.document_size,
        date_completed: formData.date_completed ? toISODateSafe(formData.date_completed) : "",
        date_expiration: formData.date_expiration ? toISODateSafe(formData.date_expiration) : "",
        date_contractor_appointment: formData.date_contractor_appointment
          ? toISODateSafe(formData.date_contractor_appointment)
          : "",
        visible_to_client: formData.visible_to_client ? 1 : 0,
      };

      console.log("🚀 [FRONTEND] Submitting certificate data:", submitData);

      // API call to create certificate
      const response = await propertiesService.storeCertificate(
        id,
        submitData
      );

      if (response.success) {
        toast.success("Certificate added successfully!");
        navigate(`/properties/${id}`);
      } else {
        throw new Error(response.message || "Failed to add certificate");
      }
    } catch (error) {
      console.error("Error adding certificate:", error);
      toast.error(error.message || "Error adding certificate");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate(`/properties/${id}`);
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <div className={styles.spinner}></div>
          <p>Loading property data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.errorContainer}>
          <h3>Error</h3>
          <p>{error}</p>
          <button className={styles.btnSecondary} onClick={handleCancel}>
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Breadcrumbs */}

      <div className="breadcrumb-row">
        <span
          className="breadcrumb-main"
          style={{ fontWeight: 400, fontSize: "14px", color: "black" }}
        >
          Properties
        </span>
        <span
          className="breadcrumb-main"
          style={{ fontWeight: 400, fontSize: "14px", color: "black" }}
        >
          {property?.property_id || `Property ${id}`}
        </span>
        <div
          style={{ background: "#30C693", fontWeight: 400, fontSize: "14px" }}
        >
          <span className="breadcrumb-sep">/</span>
          <span className="breadcrumb-properties">Add Certificate</span>
        </div>
      </div>

      <div className={styles.row}>
        <div className={styles.colMd12}>
          <div className={styles.panel}>
            <div className={styles.panelHeading}>
              <h3>Add certificate to property</h3>
            </div>
            <div className={styles.panelBody}>
              <form onSubmit={handleSubmit} className={styles.form}>
                <fieldset className={styles.fieldset}>
                  <input
                    type="hidden"
                    name="property_id"
                    value={formData.property_id}
                  />

                  {/* Name Field */}
                  <div className={styles.formGroup}>
                    <label
                      className={`${styles.controlLabel} ${styles.labelRequired}`}
                    >
                      Name
                    </label>
                    <div className={styles.colSm5}>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className={`${styles.formControl} ${
                          formErrors.name ? styles.error : ""
                        }`}
                        placeholder="Enter certificate name"
                      />
                      {formErrors.name && (
                        <span className={styles.errorText}>
                          {formErrors.name}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Document Type Field */}
                  <div className={styles.formGroup}>
                    <label
                      className={`${styles.controlLabel} ${styles.labelRequired}`}
                    >
                      Document Type
                    </label>
                    <div className={styles.colSm5}>
                      <select
                        name="document_type_id"
                        value={formData.document_type_id}
                        onChange={handleInputChange}
                        className={`${styles.formControl} ${
                          formErrors.document_type_id ? styles.error : ""
                        }`}
                        required
                      >
                        <option value="">Please Select</option>
                        {Object.entries(propertyCertificateTypes).map(
                          ([id, name]) => (
                            <option key={id} value={id}>
                              {name}
                            </option>
                          )
                        )}
                      </select>
                      {formErrors.document_type_id && (
                        <span className={styles.errorText}>
                          {formErrors.document_type_id}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Document File Field */}
                  <div className={styles.formGroup}>
                    <label
                      className={`${styles.controlLabel} ${styles.labelRequired}`}
                    >
                      Document
                    </label>
                    <div className={styles.colSm5}>
                      <div className={styles.inputImage}>
                        <input
                          type="file"
                          name="document"
                          onChange={handleFileChange}
                          className={`${styles.formControl} ${
                            formErrors.document ? styles.error : ""
                          }`}
                          accept={getDocumentAcceptValue()}
                          required
                          disabled={uploading}
                        />
                        {uploading && (
                          <div className={styles.uploadProgress}>
                            <div className={styles.progressBar}>
                              <div 
                                className={styles.progressFill}
                                style={{ width: `${uploadProgress}%` }}
                              ></div>
                            </div>
                            <span className={styles.progressText}>
                              Uploading... {Math.round(uploadProgress)}%
                            </span>
                          </div>
                        )}
                        {formData.document_name && !uploading && (
                          <div className={styles.uploadSuccess}>
                            <span className={styles.successText}>
                              ✓ {formData.document_name} uploaded successfully
                            </span>
                          </div>
                        )}
                        {formErrors.document && (
                          <span className={styles.errorText}>
                            {formErrors.document}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className={styles.clearfix}></div>

                  {/* Date Completed Field */}
                  <div className={styles.formGroup}>
                    <label className={styles.controlLabel}>
                      Date Completed
                    </label>
                    <div className={styles.colSm5}>
                      <DateInput
                        name="date_completed"
                        value={formData.date_completed}
                        onChange={handleInputChange}
                        placeholder="DD/MM/YYYY"
                        error={formErrors.date_completed}
                      />
                    </div>
                  </div>

                  {/* Date Expiration Field */}
                  <div className={styles.formGroup}>
                    <label className={styles.controlLabel}>
                      Date Expiration
                    </label>
                    <div className={styles.colSm5}>
                      <DateInput
                        name="date_expiration"
                        value={formData.date_expiration}
                        onChange={handleInputChange}
                        placeholder="DD/MM/YYYY"
                        error={formErrors.date_expiration}
                      />
                    </div>
                  </div>

                  {/* Contractor Appointment Date Field */}
                  <div className={styles.formGroup}>
                    <label className={styles.controlLabel}>
                      Contractor appointment date
                    </label>
                    <div className={styles.colSm5}>
                      <DateInput
                        name="date_contractor_appointment"
                        value={formData.date_contractor_appointment}
                        onChange={handleInputChange}
                        placeholder="DD/MM/YYYY"
                        error={formErrors.date_contractor_appointment}
                      />
                    </div>
                  </div>

                  {/* Visible to Client Field */}
                  <div className={styles.formGroup} style={{ width: "520px" }}>
                    <label className={styles.controlLabel}>
                      Visible to client
                    </label>
                    <div className={styles.colSm5}>
                      <input
                        type="checkbox"
                        name="visible_to_client"
                        checked={formData.visible_to_client}
                        onChange={handleInputChange}
                        className={styles.checkbox}
                      />
                      {formErrors.visible_to_client && (
                        <span className={styles.errorText}>
                          {formErrors.visible_to_client}
                        </span>
                      )}
                    </div>
                  </div>
                </fieldset>

                {/* Submit Button */}
                <div className={styles.formGroup}>
                  <div className={styles.textRight}>
                    <button
                      type="submit"
                      className={styles.btnHead}
                      disabled={submitting || uploading}
                    >
                      <span className={styles.glyphiconPlus}></span>
                      {submitting ? "Adding certificate..." : uploading ? "Uploading..." : "Add certificate"}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyCertificateCreate;
